import { useState, useRef } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Upload, 
  FileSpreadsheet, 
  Download,
  CheckCircle,
  AlertTriangle,
  Users,
  Phone,
  Tag,
  Calendar,
  ArrowRight,
  RefreshCw,
  Info
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivyrImportPage({ user, logout }) {
  const [clientsFile, setClientsFile] = useState(null);
  const [activitiesFile, setActivitiesFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Result
  
  const clientsRef = useRef(null);
  const activitiesRef = useRef(null);

  const handlePreview = async () => {
    if (!clientsFile) {
      toast.error("Please select the Clients CSV file");
      return;
    }

    const formData = new FormData();
    formData.append("clients_file", clientsFile);
    if (activitiesFile) {
      formData.append("activities_file", activitiesFile);
    }

    try {
      setImporting(true);
      const response = await axios.post(`${API}/import/privyr/preview`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPreviewData(response.data);
      setStep(2);
      toast.success("Preview generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!clientsFile) return;

    const formData = new FormData();
    formData.append("clients_file", clientsFile);
    if (activitiesFile) {
      formData.append("activities_file", activitiesFile);
    }
    formData.append("skip_duplicates", skipDuplicates);

    try {
      setImporting(true);
      const response = await axios.post(`${API}/import/privyr/execute`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      setImportResult(response.data);
      setStep(3);
      toast.success(`Successfully imported ${response.data.imported} leads!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setClientsFile(null);
    setActivitiesFile(null);
    setPreviewData(null);
    setImportResult(null);
    setStep(1);
    if (clientsRef.current) clientsRef.current.value = "";
    if (activitiesRef.current) activitiesRef.current.value = "";
  };

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="privyr-import-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Privyr Data Import</h1>
            <p className="text-slate-600 mt-1">Import your leads and activities from Privyr CRM</p>
          </div>
          {step > 1 && (
            <Button variant="outline" onClick={resetImport}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Start Over
            </Button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= s ? 'bg-cyan-700 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              <span className={`ml-2 text-sm font-medium ${step >= s ? 'text-slate-900' : 'text-slate-400'}`}>
                {s === 1 ? 'Upload Files' : s === 2 ? 'Preview' : 'Complete'}
              </span>
              {s < 3 && <ArrowRight className="w-4 h-4 mx-4 text-slate-300" />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-cyan-700" />
                  Client List CSV *
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    clientsFile ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-cyan-500'
                  }`}
                  onClick={() => clientsRef.current?.click()}
                >
                  <input
                    ref={clientsRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => setClientsFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  {clientsFile ? (
                    <>
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p className="font-medium text-green-700">{clientsFile.name}</p>
                      <p className="text-sm text-slate-500 mt-1">Click to change file</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="font-medium text-slate-700">Click to upload Client List CSV</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Export from Privyr: Menu → Export → Client List
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-cyan-700" />
                  Timeline Activities CSV (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    activitiesFile ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-cyan-500'
                  }`}
                  onClick={() => activitiesRef.current?.click()}
                >
                  <input
                    ref={activitiesRef}
                    type="file"
                    accept=".csv"
                    onChange={(e) => setActivitiesFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                  {activitiesFile ? (
                    <>
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p className="font-medium text-green-700">{activitiesFile.name}</p>
                      <p className="text-sm text-slate-500 mt-1">Click to change file</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="font-medium text-slate-700">Click to upload Activities CSV</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Export from Privyr: Menu → Export → Timeline Activities
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="lg:col-span-2">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">How to export from Privyr:</p>
                    <ol className="text-sm text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                      <li>Open Privyr app on your phone or web</li>
                      <li>Go to Menu → Export Data</li>
                      <li>Select "Client List" and export as CSV</li>
                      <li>Optionally export "Timeline Activities" for call notes</li>
                      <li>Upload both files here</li>
                    </ol>
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <Button 
                    className="bg-cyan-700 hover:bg-cyan-800"
                    onClick={handlePreview}
                    disabled={!clientsFile || importing}
                  >
                    {importing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Generate Preview
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 2 && previewData && (
          <div className="space-y-6">
            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="w-8 h-8 mx-auto mb-2 text-cyan-700" />
                  <p className="text-3xl font-bold text-slate-900">{previewData.stats.total_leads}</p>
                  <p className="text-sm text-slate-500">Total Leads</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Phone className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-3xl font-bold text-slate-900">{previewData.stats.with_notes}</p>
                  <p className="text-sm text-slate-500">With Notes</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Tag className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-3xl font-bold text-slate-900">{Object.keys(previewData.stats.by_source).length}</p>
                  <p className="text-sm text-slate-500">Sources</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                  <p className="text-3xl font-bold text-slate-900">{previewData.activities_count || 0}</p>
                  <p className="text-sm text-slate-500">Activities</p>
                </CardContent>
              </Card>
            </div>

            {/* By Source */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Leads by Source</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(previewData.stats.by_source).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium capitalize">{source.replace("_", " ")}</span>
                      <Badge>{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Disposition */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Leads by Disposition</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(previewData.stats.by_disposition).map(([disp, count]) => (
                    <div key={disp} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <span className="font-medium capitalize">{disp.replace("_", " ")}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Sample Leads */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sample Leads (First 5)</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Disposition</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.sample_leads.map((lead, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{lead.name}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{lead.source}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="capitalize">{lead.disposition?.replace("_", " ")}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {lead.created_at?.split("T")[0]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Import Options */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox 
                      id="skip-duplicates" 
                      checked={skipDuplicates}
                      onCheckedChange={setSkipDuplicates}
                    />
                    <label htmlFor="skip-duplicates" className="text-sm font-medium">
                      Skip duplicate phone numbers (recommended)
                    </label>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button 
                      className="bg-cyan-700 hover:bg-cyan-800"
                      onClick={handleImport}
                      disabled={importing}
                    >
                      {importing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Importing...
                        </>
                      ) : (
                        <>
                          Import {previewData.stats.total_leads} Leads
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 3 && importResult && (
          <div className="space-y-6">
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h2 className="text-2xl font-bold text-green-800">Import Complete!</h2>
                <p className="text-green-600 mt-2">
                  Successfully imported {importResult.imported} leads into your CRM
                </p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-green-600">{importResult.imported}</p>
                  <p className="text-slate-500 mt-1">Imported</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-yellow-600">{importResult.skipped}</p>
                  <p className="text-slate-500 mt-1">Skipped (Duplicates)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <p className="text-4xl font-bold text-red-600">{importResult.errors}</p>
                  <p className="text-slate-500 mt-1">Errors</p>
                </CardContent>
              </Card>
            </div>

            {importResult.error_details?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Import Errors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-red-600">
                    {importResult.error_details.map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={resetImport}>
                Import More Data
              </Button>
              <Button 
                className="bg-cyan-700 hover:bg-cyan-800"
                onClick={() => window.location.href = "/leads"}
              >
                View Leads
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
